import React, { useState, useEffect } from 'react';
import { Users, Target, Zap, Heart, Award, Globe, ArrowRight, Star } from 'lucide-react';

const WhoWeAreSection = () => {
  const [currentStat, setCurrentStat] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setCurrentStat((prev) => (prev + 1) % 4);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const stats = [
    { value: "500+", label: "Happy Clients", icon: Users },
    { value: "99%", label: "Success Rate", icon: Target },
    { value: "24/7", label: "Support", icon: Heart },
    { value: "5★", label: "Average Rating", icon: Star }
  ];

  const values = [
    {
      icon: Zap,
      title: "Innovation First",
      description: "We push boundaries and embrace cutting-edge solutions that transform possibilities into reality."
    },
    {
      icon: Heart,
      title: "Customer Obsessed",
      description: "Every decision we make starts with understanding and exceeding our customers' expectations."
    },
    {
      icon: Award,
      title: "Excellence Driven",
      description: "We deliver nothing short of exceptional quality in everything we create and every service we provide."
    },
    {
      icon: Globe,
      title: "Global Impact",
      description: "Our vision extends beyond borders, creating solutions that make a meaningful difference worldwide."
    }
  ];

  const teamMembers = [
    {
      name: "Sarah Chen",
      role: "CEO & Founder",
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b577?w=400&h=400&fit=crop&crop=face",
      quote: "Building the future, one innovation at a time."
    },
    {
      name: "Marcus Rodriguez",
      role: "CTO",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face",
      quote: "Technology is only as powerful as the people behind it."
    },
    {
      name: "Emma Thompson",
      role: "Head of Design",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop&crop=face",
      quote: "Great design is invisible until it transforms lives."
    }
  ];

  return (
    
    <div className="relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-black">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-40"></div>
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
      </div>
      

      <div className="relative z-10 container mx-auto px-6 py-24">
        {/* Main Hero Section */}
        <div className={`text-center mb-20 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="inline-block mb-6">
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 text-transparent bg-clip-text text-lg font-semibold tracking-wide uppercase">
              Who We Are
            </span>
          </div>
          
          <h2 className=" md:text-5xl font-bold text-white mb-8 leading-tight">
            We Are
            <span className="text-7xl block bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 text-transparent bg-clip-text">
              Innovators
            </span>
            <span className=" md:text-5xl text-slate-300">& Difference Makers</span>
          </h2>
          
          <p className="text-xl md:text-xl text-slate-300 max-w-4xl mx-auto leading-relaxed mb-12 font-poppins">
            Born from a vision to revolutionize the digital landscape, we're a passionate team of innovators, 
            creators, and problem-solvers dedicated to crafting extraordinary experiences that shape tomorrow.
          </p>

          {/* Animated Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  className={`text-center transform transition-all duration-700 ${
                    currentStat === index ? 'scale-110' : 'scale-100'
                  }`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 transition-all duration-500 ${
                    currentStat === index 
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 shadow-2xl' 
                      : 'bg-white/10 backdrop-blur-sm'
                  }`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-3xl font-bold text-white mb-2">{stat.value}</div>
                  <div className="text-slate-400 text-sm font-medium">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Our Story Section */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
          <div className="order-2 lg:order-1">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=600&fit=crop"
                alt="Our team collaborating"
                className="rounded-3xl shadow-2xl transform hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute -bottom-6 -right-6 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 rounded-2xl shadow-2xl">
                <div className="text-2xl font-bold">2019</div>
                <div className="text-sm">Founded</div>
              </div>
            </div>
          </div>
          
          <div className="order-1 lg:order-2">
            <h3 className="text-4xl font-bold text-white mb-6">
              Our Story Began With a
              <span className="block bg-gradient-to-r from-purple-400 to-pink-400 text-transparent bg-clip-text">
                Simple Belief
              </span>
            </h3>
            <p className="text-lg text-slate-300 mb-6 leading-relaxed font-poppins">
              That technology should empower, not complicate. What started as a small team of dreamers 
              in a garage has evolved into a global force for digital transformation, touching millions 
              of lives across continents.
            </p>
            <p className="text-lg text-slate-300 mb-8 leading-relaxed font-poppins">
              Today, we're proud to be at the forefront of innovation, creating solutions that don't 
              just meet today's needs—they anticipate tomorrow's possibilities.
            </p>
            <button className="group bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center">
              Learn Our Journey
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>

        {/* Values Grid */}
        <div className="mb-24">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-white mb-6">
              What Drives Us
            </h3>
            <p className="text-lg text-slate-300 max-w-3xl mx-auto font-poppins">
              Our core values aren't just words on a wall—they're the foundation of every decision, 
              every product, and every relationship we build.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 hover:bg-white/10 transition-all duration-500 hover:scale-105 hover:shadow-2xl"
                >
                  <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-white mb-4 group-hover:text-purple-300 transition-colors">
                    {value.title}
                  </h4>
                  <p className="text-slate-400 leading-relaxed group-hover:text-slate-300 transition-colors">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Team Section */}
        {/* <div className="text-center"> */}
          {/* <h3 className="text-4xl font-bold text-white mb-6">
            Meet the Visionaries
          </h3>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-16">
            Behind every great company are exceptional people. Meet the leaders who are 
            shaping our future and driving innovation every single day.
          </p>
          
          <div className="grid md:grid-cols-3 gap-12 max-w-5xl mx-auto">
            {teamMembers.map((member, index) => (
              <div
                key={index}
                className="group text-center transform hover:scale-105 transition-all duration-500"
              >
                <div className="relative mb-6 inline-block">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-32 h-32 rounded-full object-cover shadow-2xl border-4 border-white/20 group-hover:border-purple-400 transition-all duration-300"
                  />
                  <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full border-4 border-slate-900"></div>
                </div>
                <h4 className="text-xl font-bold text-white mb-2 group-hover:text-purple-300 transition-colors">
                  {member.name}
                </h4>
                <p className="text-purple-400 font-medium mb-4">
                  {member.role}
                </p>
                <p className="text-slate-400 italic group-hover:text-slate-300 transition-colors">
                  "{member.quote}"
                </p>
              </div>
            ))}
          </div> */}

          {/* CTA */}
          {/* <div className="mt-16">
            <button className="group bg-gradient-to-r from-white to-slate-100 hover:from-purple-50 hover:to-pink-50 text-slate-900 font-bold py-4 px-12 rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-2xl flex items-center mx-auto">
              Join Our Mission
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform text-purple-600" />
            </button>
          </div> */}
        {/* </div> */}
      </div>
    </div>
    
  );
};

export default WhoWeAreSection;